<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import axiosInstance from '@/plugins/axios';
import { computedAsync } from '@vueuse/core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from "primevue/useconfirm";

const allUsers = ref([]);
const selectedUser = ref();
const displayDialog = ref(false);
const toast = useToast();
const confirm = useConfirm();
const availableLanguages = ref([])

const isLoading = ref(false)
const createInviteLinkModalVisible = ref(false)
const inviteLinkSettings = ref({
    expirationDate: new Date(new Date().getTime() + 7 * 24 * 60 * 60 * 1000),
    role: '',
    tenant: '',
    email: ''
})
const inviteLink = ref()
const userRoles = ref([

    {
        label: 'ai_lab_admin',
        value: 'ai_lab_admin',
        help: 'Administrator of the AI Lab with full access to all features, including configuration settings.'
    },
    {
        label: 'ai_lab_user',
        value: 'ai_lab_user',
        help: 'User of the AI Lab with access to all features except configuration settings.'
    },
    {
        label: 'mandant_admin',
        value: 'mandant_admin',
        help: 'Administrator of a tenant organization with full access to all tenant-specific settings and resources.'
    },
    {
        label: 'mandant_user',
        value: 'mandant_user',
        help: 'User of a tenant organization with restricted access; cannot modify tenant-specific configuration settings likewise CI-Settings.'
    }
])
const allTenants = ref([])

const minifiedTranslation = computedAsync(async () => {
    try {
        const res = await axiosInstance.get('/admin/getAllTranslationsMinified');
        return res.data;
    } catch (e) {
        console.error(e);
        return [];
    }
}, []);


const getAllTenants = async () => {
    try {
        const res = await axiosInstance.get('/admin/getAllTenants', {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
        })
        allTenants.value = res.data
    } catch (e) {
        console.error(e)
    }
}

const getAllUsers = async () => {
    try {
        const res = await axiosInstance.get('/user/', {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
        })
        allUsers.value = res.data
    } catch (e) {
        console.error(e)
    }
}

const showEditDialog = (user: Array) => {
    selectedUser.value = { ...user };
    displayDialog.value = true;
};

const saveUser = async () => {
    isLoading.value = true
    try {
        const response = await axiosInstance.put(`/user/update/${selectedUser.value._id}`, selectedUser.value, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
        });

        console.log("Erfolgreich aktualisiert:", response.data);

        await getAllUsers();

        toast.add({
            severity: 'success',
            summary: 'Erfolgreich',
            detail: 'Der Benutzer wurde aktualisiert.',
            life: 3000
        });
        isLoading.value = false
    } catch (error) {
        console.error("Fehler bei der Aktualisierung des Benutzers:", error.response);
        toast.add({
            severity: 'error',
            summary: 'Fehler',
            detail: 'Aktualisierung fehlgeschlagen',
            life: 3000
        });
    }
    isLoading.value = false
    displayDialog.value = false;
};

const getTenantNameById = (id: string) => {
    const tenant = allTenants.value.find(tenant => tenant._id === id)
    return tenant ? tenant.name : 'N/A'
}

const copyInviteLinkToClipboard = () => {
    navigator.clipboard.writeText(inviteLink.value)
    toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Link successfully copied to clipboard.',
        life: 3000
    });
    createInviteLinkModalVisible.value = false
    inviteLink.value = ''
    
}

const createInviteLink = async () => {
    isLoading.value = true
    try {
        const response = await axiosInstance.post('/admin/createInviteLink', inviteLinkSettings.value)


        inviteLink.value = response.data

        toast.add({
            severity: 'success',
            summary: 'Erfolgreich',
            detail: 'Der Einladungslink wurde erstellt.',
            life: 3000
        });
        isLoading.value = false
    } catch (error) {
        console.error("Fehler bei der Erstellung des Einladungslinks:", error.response);
        toast.add({
            severity: 'error',
            summary: 'Fehler',
            detail: 'Erstellung fehlgeschlagen',
            life: 3000
        });
    } finally{
            isLoading.value = false
    inviteLinkSettings.value = {
        expirationDate: new Date(),
        role: '',
        tenant: '',
        email: ''
    }
    }

};


watch(() => selectedUser.value?.tenant, async (newTenant) => {
    if (newTenant) {
        try {
            const res = await axiosInstance.get(`/admin/getTenant/${newTenant}`)
            console.log('Tenant data:', res.data.adminSettings.enabledLanguages)
            console.log("minifiedTranslation:", minifiedTranslation.value)  
            availableLanguages.value = res.data.adminSettings.enabledLanguages.map((lang: any) => ({
                _id: lang,
                name: minifiedTranslation.value.find(translation => translation._id === lang)?.languageIdentifier || lang,
                languageCode: minifiedTranslation.value.find(translation => translation._id === lang)?.languageCode || lang
            }))

            console.log('Available languages:', availableLanguages.value)
        } catch (e) {
            console.error(e)
        }
    }
})

const deleteUser = async (userId: any) => {
    isLoading.value = true
    try {
        const response = await axiosInstance.delete(`/user/${userId}`, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
        });


        await getAllUsers();

        toast.add({
            severity: 'success',
            summary: 'Erfolgreich',
            detail: 'Der Benutzer wurde gelöscht.',
            life: 3000
        });
    } catch (error) {
        console.error("Fehler beim Löschen des Benutzers:", error.response);
        toast.add({
            severity: 'error',
            summary: 'Fehler',
            detail: 'Löschen fehlgeschlagen',
            life: 3000
        });
    } finally {
        isLoading.value = false
    }
}

const requireConfirmation = (event: any, userId: any) => {
    confirm.require({
        target: event.currentTarget,
        group: 'headless',
        message: 'Do you really want to delete this user?',
        accept: () => {
            deleteUser(userId);
        },
        reject: () => {
            console.log('User deletion cancelled');
        }
    });
}

onMounted(async () => {
    await getAllUsers()
    await getAllTenants()
})

</script>


<template>
    <div>
        <ConfirmPopup group="headless">
        <template #container="{ message, acceptCallback, rejectCallback }">
            <div class="rounded p-4">
                <span>{{ message.message }}</span>
                <div class="flex items-center gap-2 mt-4">
                    <Button label="Delete" @click="acceptCallback" size="small" class="p-button p-button-danger"></Button>
                    <Button label="Cancel" outlined @click="rejectCallback" severity="secondary" size="small" text></Button>
                </div>
            </div>
        </template>
    </ConfirmPopup>

        <div class="surface-section px-4 py-5 ">
            <div class="flex align-items-start flex-column lg:justify-content-between lg:flex-row">
                <div>
                    <div class="flex align-items-center text-700 flex-wrap">
                        <div class="mr-5 flex align-items-center mt-3">
                            <font-awesome-icon :icon="['fas', 'check']" />&nbsp;
                            <span>{{allUsers.filter(user => user.active).length}} aktive Benutzer</span>
                        </div>
                        <div class="mr-5 flex align-items-center mt-3">
                            <font-awesome-icon :icon="['fas', 'x']" />&nbsp;
                            <span>{{allUsers.filter(user => !user.active).length}} inaktive Benutzer</span>
                        </div>
                    </div>
                </div>
                <div class="mt-3 lg:mt-0">
                    <Button severity="info" label="Create Invite Link" class="p-button-outlined mr-2"
                        @click="createInviteLinkModalVisible = true">
                        <template #icon>
                            <font-awesome-icon :icon="['fas', 'paper-plane']" />&nbsp;
                        </template>
                    </Button>
                </div>
            </div>
        </div>
        <div>
            <DataTable :value="allUsers" dataKey="id" paginator :rows="10">
                <Column field="firstName" header="Name"></Column>
                <Column field="email" header="E-Mail"></Column>
                <Column field="role" header="Role"></Column>
                <Column field="tenant" header="Tenant">
                    <template #body="slotProps">
                        {{ getTenantNameById(slotProps.data.tenant) }}
                    </template>
                </Column>
                <Column field="active" header="Status">
                    <template #body="slotProps">
                        <Tag v-if="slotProps.data.active" severity="success">Active</Tag>
                        <Tag v-else severity="danger">Inactive</Tag>
                    </template>
                </Column>
                <Column header="Aktionen">
                    <template #body="slotProps">
                        <Button @click="showEditDialog(slotProps.data)" class="p-button-tuev mr-2">
                            <template #icon>
                                <font-awesome-icon :icon="['fas', 'edit']" />
                            </template>
                        </Button>
                        <Button @click="requireConfirmation($event, slotProps.data._id)" class="p-button-danger">
                            <template #icon>
                                <font-awesome-icon :icon="['fas', 'trash']" />
                            </template>
                        </Button>
                    </template>
                </Column>
            </DataTable>
        </div>
        <Dialog v-model:visible="displayDialog" :style="{ width: '50vw' }" modal header="Edit User">
            <div class="p-fluid">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-4">
                        <label for="activeToggle">Status</label>
                    </div>
                    <div class="field col-12 md:col-8">
                        <ToggleButton id="activeToggle" v-model="selectedUser.active" onLabel="Enabled"
                            offLabel="Disabled" class="w-full" />
                    </div>

                    <div class="field col-12 md:col-4">
                        <label for="firstName">First Name</label>
                    </div>
                    <div class="field col-12 md:col-8">
                        <InputText id="firstName" v-model="selectedUser.firstName" class="w-full" />
                    </div>

                    <div class="field col-12 md:col-4">
                        <label for="lastName">Last Name</label>
                    </div>
                    <div class="field col-12 md:col-8">
                        <InputText id="lastName" v-model="selectedUser.lastName" class="w-full" />
                    </div>

                    <div class="field col-12 md:col-4">
                        <label for="email">Email</label>
                    </div>
                    <div class="field col-12 md:col-8">
                        <InputText id="email" v-model="selectedUser.email" class="w-full" />
                    </div>

                    <div class="field col-12 md:col-4">
                        <label for="role">Role</label>
                    </div>
                    <div class="field col-12 md:col-8 d-flex align-items-center gap-2">
                        <Select id="role" v-model="selectedUser.role" :options="userRoles" optionLabel="label"
                            optionValue="value" class="w-full flex-grow-1" />
                        <Button
                            v-tooltip.top="userRoles.find(role => role.value === selectedUser.role)?.help || 'No help available'"
                            class="p-button-outlined p-button-sm p-button-info" rounded>
                            <template #icon>
                                <font-awesome-icon :icon="['fas', 'question']" />
                            </template>
                        </Button>
                    </div>


                    <div class="field col-12 md:col-4">
                        <label for="tenant">Tenant</label>
                    </div>
                    <div class="field col-12 md:col-8">
                        <Select id="tenant" v-model="selectedUser.tenant" :options="allTenants" optionLabel="name"
                            optionValue="_id" class="w-full" />
                    </div>

                    <div class="field col-12 md:col-4" v-if="selectedUser.role === 'mandant_user'">
                        <label for="tenant">Editable Languages</label>
                    </div>
                    <div class="field col-12 md:col-8" v-if="selectedUser.role === 'mandant_user'">
                        <MultiSelect id="languages" v-model="selectedUser.availableLanguages" :options="availableLanguages" optionLabel="name"
                            optionValue="_id" class="w-full" placeholder="Select languages" />
                    </div>

                    <div class="field col-12 d-flex justify-content-end mt-4">
                        <Button label="Save" class="p-button-tuev" @click="saveUser" :loading="isLoading">
                            <template #icon>
                                <font-awesome-icon :icon="['fas', 'save']" />
                            </template>
                        </Button>
                    </div>
                </div>
            </div>
        </Dialog>

        <Dialog v-model:visible="createInviteLinkModalVisible" :style="{ width: '50vw' }" modal
            header="Create Invite Link">
            <div v-if="!inviteLink">
                <div class="form-field w-100 mt-3">
                    <FloatLabel variant="on">
                        <Calendar fluid v-model="inviteLinkSettings.expirationDate" inputId="on_label" />
                        <label for="on_label">Expiration Date</label>
                    </FloatLabel>
                </div>
                <div class="form-field w-100 mt-3">
                    <FloatLabel variant="on">
                        <Select id="role" v-model="inviteLinkSettings.role" :options="userRoles" optionLabel="label"
                            optionValue="value" class="w-full" inputId="on_label" />
                        <label for="on_label">User Role</label>
                    </FloatLabel>
                </div>
                <div class="form-field w-100 mt-3">
                    <FloatLabel variant="on">
                        <Select id="tenant" v-model="inviteLinkSettings.tenant" :options="allTenants" optionLabel="name"
                            optionValue="_id" class="w-full" inputId="on_label" />
                        <label for="on_label">Tenant</label>
                    </FloatLabel>
                </div>
                <div class="form-field w-100 mt-3">
                    <FloatLabel variant="on">
                        <InputText id="tenant" v-model="inviteLinkSettings.email"  class="w-full" />
                        <label for="on_label">Mail (optional)</label>
                    </FloatLabel>
                </div>
                <div class="field col-12 d-flex justify-content-end mt-4">
                    <Button label="Create" class="p-button-tuev" @click="createInviteLink" :loading="isLoading">
                        <template #icon>
                            <font-awesome-icon :icon="['fas', 'save']" />
                        </template>
                    </Button>
                </div>
            </div>
            <div v-else>
                <div>
                    <div class="">
                        <p>Successfully created invite link!</p>

                        <Message severity="secondary" class="linkMessage mx-5 my-2">
                            {{ inviteLink || 'No Link available' }}
                        </Message>
                        <div class="col-12 d-flex justify-content-end">
                            <Button class="p-button-tuev" label="Copy and Close" @click="copyInviteLinkToClipboard">
                                <template #icon>
                                    <font-awesome-icon :icon="['fas', 'copy']" />
                                </template>
                            </Button>
                        </div>


                    </div>
                </div>

            </div>
        </Dialog>
    </div>

</template>


<style>
.linkMessage {
    word-break: break-all;
}
</style>